<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Article`.trim()" icon="mdi-newspaper-variant" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <div class="form-section-title">
                    <v-icon small>mdi-text-box-outline</v-icon>Details
                </div>
                <v-row>
                    <v-col cols="12" md="8">
                        <v-text-field
                            v-model="article.title"
                            outlined
                            dense
                            hide-details="auto"
                            label="Title"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <date-time-picker
                            :date-label="'Article Date'"
                            :date="articleDateTime"
                            @setDate="setArticleDatetime"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-image-multiple-outline</v-icon>Media
                </div>
                <v-row>
                    <v-col cols="12" md="6">
                        <image-upload
                            v-model="image"
                            :src="imagePath"
                            label="Cover image"
                            :readonly="readOnly"
                            @change="changeImage"
                            @clear="clearImage"
                        />
                    </v-col>
                    <v-col v-if="!readOnly" cols="12" md="6">
                        <div class="text-caption article-file-label">Attachment</div>
                        <div class="d-flex align-center">
                            <v-file-input
                                v-model="file"
                                outlined
                                dense
                                hide-details="auto"
                                accept="pdf/*"
                                show-size
                                label="Article File"
                                prepend-icon=""
                                prepend-inner-icon="mdi-paperclip"
                                :clearable="clearable"
                                @change="changeFile"
                                @click:clear="clearFile()"
                            />
                            <v-btn
                                class="ml-2 white--text"
                                depressed
                                color="black"
                                :disabled="!file"
                                @click="uploadFile()"
                            >
                                <v-icon left small>mdi-upload</v-icon>Upload
                            </v-btn>
                        </div>
                        <div class="text-caption grey--text mt-2">
                            Upload a file to get a link you can use inside the content.
                        </div>
                        <v-text-field
                            v-if="fileUrl"
                            :value="fileUrl"
                            class="mt-3"
                            outlined
                            dense
                            readonly
                            hide-details
                            label="File link"
                            prepend-inner-icon="mdi-link-variant"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-image-multiple</v-icon>Gallery
                    <v-chip v-if="article.gallery && article.gallery.length" x-small dark color="primary" class="ml-1">{{ article.gallery.length }}</v-chip>
                </div>
                <v-row>
                    <v-col v-if="!readOnly" cols="12" md="6">
                        <v-file-input
                            v-model="galleryFiles"
                            outlined
                            dense
                            hide-details="auto"
                            accept="image/png, image/jpeg"
                            multiple
                            small-chips
                            show-size
                            label="Add photos"
                            prepend-icon=""
                            prepend-inner-icon="mdi-image-plus"
                            :clearable="clearable"
                            @change="changeGalleryFiles"
                            @click:clear="clearGalleryFiles()"
                        />
                        <div class="text-caption grey--text mt-2">
                            The photos are uploaded when the article is saved.
                        </div>
                    </v-col>
                </v-row>
                <div v-if="(article.gallery && article.gallery.length) || galleryPreviews.length" class="article-gallery">
                    <div v-for="(galleryImage, index) in article.gallery" :key="`gallery-${index}`" class="article-gallery__item">
                        <v-img :src="googleApiHost + '/' + galleryImage" aspect-ratio="1.4" cover />
                        <v-btn v-if="!readOnly" class="article-gallery__remove" fab x-small dark color="red darken-1" @click="removeGalleryImage(index)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <div v-for="(preview, index) in galleryPreviews" :key="`gallery-preview-${index}`" class="article-gallery__item article-gallery__item--new">
                        <v-img :src="preview" aspect-ratio="1.4" cover />
                        <span class="article-gallery__badge">New</span>
                    </div>
                </div>

                <div class="form-section-title">
                    <v-icon small>mdi-format-paragraph</v-icon>Content
                </div>
                <!-- the editor loads on demand: the content is shown as a preview until "Edit content" -->
                <lazy-editor
                    v-model="article.content"
                    editor-id="article_content"
                    label="Content"
                    :read-only="readOnly"
                    :init="editorInit"
                />
            </v-form>
        </v-card>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import ImageUpload from '@/components/ImageUpload'
    import articleService from '@/services/article'
    import uploadService from '@/services/upload'
    import Alert from '@/components/Alert'
    import DateTimePicker from '@/components/DateTimePicker'
    import momentTimezone from 'moment-timezone'
    import LazyEditor from '@/components/LazyEditor'
    export default {
        name: 'ArticleForm',
        components: { Alert, DateTimePicker, LazyEditor, PageHeader, ImageUpload },
        data: () => {
            return {
                showAlert: false,
                message: null,
                type: null,
                mode: null,
                googleApiHost: 'https://storage.googleapis.com',
                readOnly: false,
                clearable: true,
                loading: false,
                startDate: null,
                endDate: null,
                fileUrl: '',
                article: {
                    gallery: []
                },
                galleryFiles: [],
                galleryPreviews: [],
                articleId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                image: {
                    name: 'Image file'
                },
                imageChanged: false,
                imagePath: '',
                file: {
                    name: 'File'
                },
                filePath: '',
                articleDateTime: null,
                editorInit: {
                    images_upload_handler: function(file, success) {
                        let formData = new FormData()
                        formData.append('file', file.blob(), file.filename())
                        uploadService.uploadFile(formData).then((response) => { success(process.env.VUE_APP_GOOGLE_STORAGE_API + '/' + response.data.content) })
                    },
                    menubar: false,
                    min_height: 600,
                    plugins: [
                        'advlist autolink lists charmap print preview anchor',
                        'searchreplace visualblocks fullscreen',
                        'insertdatetime table paste code help wordcount link',
                        'image code media'
                    ],
                    automatic_uploads: true,
                    powerpaste_allow_local_images: true,
                    file_picker_types: 'image',
                    toolbar: 'undo redo | styleselect | forecolor | bold italic | fontselect | emoticons | alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | link unlink | image media | code '
                }
            }
        },
        async created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.articleId = this.$route.params.articleId
            if (this.articleId) {
                articleService.getArticle(this.articleId).then(resp => {
                    this.article = { ...resp.data, gallery: resp.data.gallery || [] }
                    this.articleDateTime = momentTimezone(this.article.date).tz('Europe/Athens').format('YYYY-MM-DD').toString()

                    if (this.article.image) {
                        this.imagePath = this.googleApiHost + '/' + this.article.image
                    }
                })
            }
        },
        methods: {
            changeFile() {
                this.fileChanged = true
                if (this.file) {
                    this.filePath = URL.createObjectURL(this.file)
                }
            },
            changeGalleryFiles() {
                this.galleryPreviews = (this.galleryFiles || []).map(file => URL.createObjectURL(file))
            },
            clearGalleryFiles() {
                this.galleryFiles = []
                this.galleryPreviews = []
            },
            removeGalleryImage(index) {
                this.article.gallery.splice(index, 1)
            },
            uploadGalleryImages() {
                return Promise.all((this.galleryFiles || []).map(file => {
                    const formData = new FormData()
                    formData.append('file', file)
                    return articleService.uploadFile(formData)
                }))
            },
            clearFile() {
                this.filePath = ''
            },
            changeImage() {
                this.imageChanged = true
                if (this.image) {
                    this.imagePath = URL.createObjectURL(this.image)
                }
            },
            clearImage() {
                this.imagePath = ''
                this.article.image = null
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                return false
            },
            setArticleDatetime(date) {
                this.articleDateTime = date
            },
            onEdit() {
                this.$router.push(`/articles/${this.articleId}/edit`)
            },
            createArticle() {
                articleService.createArticle(this.article).then(articleResponse => {
                    if (articleResponse.id) {
                        this.articleId = articleResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/articles`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            editArticle() {
                articleService.updateArticle(this.articleId, this.article).then(articleResponse => {
                    if (articleResponse.id) {
                        this.articleId = articleResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/articles`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            uploadFile() {
                if (!this.fileChanged || this.file == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.file)
                uploadService.uploadFile(formData).then((response) => { this.fileUrl = this.googleApiHost + '/' + response.data.content })
            },
            uploadImage() {
                if (!this.imageChanged || this.image == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.image)
                return articleService.uploadFile(formData)
            },
            async onSubmit() {
                this.loading = true
                this.article.date = momentTimezone(this.articleDateTime).tz('Europe/Athens').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]').toString()

                const uploadImageResponse = await this.uploadImage()
                const imageUrl = uploadImageResponse?.data.content || null
                if (imageUrl) {
                    this.article.image = imageUrl
                }
                try {
                    const galleryResponses = await this.uploadGalleryImages()
                    galleryResponses.forEach(resp => {
                        if (resp && resp.data && resp.data.content) {
                            this.article.gallery.push(resp.data.content)
                        }
                    })
                    this.clearGalleryFiles()
                } catch (error) {
                    this.loading = false
                    this.setAlert('Gallery image upload failed', 'error')
                    return
                }
                if (this.mode === 'edit') {
                    this.editArticle()
                } else if (this.mode === 'create') {
                    this.createArticle()
                }
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.article-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
    margin-top: 12px;
}
.article-gallery__item {
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
}
.article-gallery__item--new {
    outline: 2px dashed #009688;
    outline-offset: -2px;
}
.article-gallery__remove {
    position: absolute !important;
    top: 6px;
    right: 6px;
}
.article-gallery__badge {
    position: absolute;
    left: 6px;
    bottom: 6px;
    padding: 1px 8px;
    border-radius: 10px;
    background: #009688;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 600;
}
.article-file-label {
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 4px;
}

.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
